/**
 * P0-B — cricket-intent carve-out for the dream gate.
 *
 * Recognizes a sufficiently SPECIFIC, ACTIONABLE cricket problem/question so
 * the player's first coaching exchange can happen BEFORE onboarding (the
 * dream picker), instead of the dream gate swallowing the message.
 *
 * Design (deliberately NOT a brittle handful of keywords):
 *   - BROAD entity coverage across batting / bowling / fielding /
 *     wicketkeeping / match situations / training (~120 phrases).
 *   - Specificity bar: >= 40 chars AND >= 2 distinct cricket entities AND
 *     (a problem marker OR a question shape). Vague messages ("hi",
 *     "how do I get better at cricket?", "I play cricket on weekends")
 *     do NOT clear the bar, so they keep the normal onboarding path.
 *   - Explicit exclusions: non-cricket lookalikes (baseball), and
 *     shopping/pricing intents (buy/price/cost) which are not coaching.
 *
 * Conservative by construction: false negatives fall back to the existing
 * dream gate (safe); false positives just get a coaching attempt first,
 * with the dream picker deferred — never dropped.
 */

const CRICKET_ENTITIES = [
  // batting
  "batting", "batsman", "batter", "opener", "opening", "stance", "guard",
  "backlift", "trigger", "footwork", "front foot", "back foot",
  "cover drive", "straight drive", "on drive", "off drive", "pull", "hook",
  "cut", "square cut", "late cut", "sweep", "reverse sweep", "flick",
  "glance", "defend", "defence", "defense", "leave", "lofted", "slog",
  "slog sweep", "switch hit",
  // bowling
  "bowling", "bowler", "bowl", "ball", "over", "overs", "yorker", "bouncer",
  "slower ball", "off cutter", "leg cutter", "seam", "swing", "reverse swing",
  "spin", "legspin", "leg spin", "offspin", "off spin", "googly", "doosra",
  "carrom", "flipper", "slider", "flight", "drift", "dip", "length",
  "good length", "short of a length", "full toss", "beamer", "wide",
  "no ball", "bowling action", "action", "run up", "runup", "gather",
  "release", "wrist", "seam position", "pace", "speed", "variation",
  "inswinger", "outswinger",
  // dismissals / scoring
  "caught", "behind", "bowled", "lbw", "run out", "stumped", "hit wicket",
  "duck", "century", "fifty", "half century", "not out", "strike",
  // fielding
  "fielding", "fielder", "catch", "catches", "catching", "slip", "gully",
  "point", "cover", "mid off", "mid on", "midwicket", "square leg",
  "fine leg", "third man", "short leg", "long on", "long off", "deep",
  "throw", "throwing", "shy at the stumps", "dive", "diving", "stop",
  "boundary", "patrol",
  // wicketkeeping
  "wicketkeeper", "wicketkeeping", "keeper", "gloves", "leg side",
  "standing up", "standing back", "byes",
  // match situations
  "match", "innings", "test match", "odi", "t20", "powerplay",
  "death overs", "chase", "target", "partnership", "collapse", "tail",
  "middle order", "top order", "captain", "captaincy", "toss", "pitch",
  "crease", "review", "session",
  // training
  "nets", "net session", "practice", "drill", "drills", "shadow batting",
  "throwdowns", "sidearm", "video", "footage",
  // body / performance
  "shoulder", "elbow", "knee", "ankle", "hamstring", "side strain",
  "injury", "fitness", "strength", "stamina", "warm up", "warmup",
  // the game itself (counts as an entity, but never enough alone)
  "cricket", "cricketer",
];

const PROBLEM_MARKERS = [
  "i keep", "i'm struggling", "struggling with", "struggle with",
  "struggle to", "problem", "issue with", "wrong with", "not working", "doesn't work",
  "don't work", "doesnt work", "dont work", "help me", "help with",
  "advice", "tips", "tip ", "suggest", "recommend", "how do i",
  "how can i", "how should i", "what should i", "what can i", "work on",
  "improve", "fix", "stop ", "keep getting", "can't", "cannot",
  "unable to", "difficult", "hard to", "every time", "each time",
  "go wrong", "mistake", "worried", "nervous", "confidence", "afraid",
  "fear", "frustrat",
];

// Exclusions: messages that look cricket-adjacent but are NOT coaching
// intent (gear shopping, other sports, pricing). The buy pattern is
// deliberately narrow — the bare word collides with the cricket idiom
// "can't buy a wicket", so only unambiguous purchase phrasing counts.
const EXCLUSIONS = [
  "baseball",
  /should i buy|to buy|buying|\bbuy (new|some)\b/,
  /\bprice\b/, /\bcost\b/, /\border\b/, /\bshop\b/,
  /\bdiscount\b/,
];

const QUESTION_SHAPE =
  /^(what|how|why|when|where)\s+(should|do|does|can|could|is|are|would|will|have|has)\b/i;
const YN_QUESTION =
  /^(is|are|do|does|should|can|could|will|would|have|has)\b/i;

export function looksLikeSpecificCricketProblem(text) {
  const t = (text || "").trim();
  if (t.length < 40) return false;
  const lower = t.toLowerCase();

  for (const ex of EXCLUSIONS) {
    if (typeof ex === "string") {
      if (lower.includes(ex)) return false;
    } else if (ex.test(lower)) {
      return false;
    }
  }

  let entities = 0;
  for (const e of CRICKET_ENTITIES) {
    if (lower.includes(e)) {
      entities += 1;
      if (entities >= 2) break;
    }
  }
  if (entities < 2) return false;

  if (PROBLEM_MARKERS.some((m) => lower.includes(m))) return true;
  if (QUESTION_SHAPE.test(t) || YN_QUESTION.test(t)) return true;
  if (/\?\s*$/.test(t)) return true;
  return false;
}

export default looksLikeSpecificCricketProblem;
